import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, UploadCloud, Info, Eye, 
  Sparkles, Zap, Droplet, Users, Wifi, Map as MapIcon, Star, X, Music, Globe, ArrowRight,
  Instagram, Facebook, Linkedin, Twitter, Youtube, Link as LinkIcon, Calendar
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FestivalCreationHeader } from '../components/FestivalCreationHeader';

export default function CreateFestivalStep2Page() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Form State
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [gallery, setGallery] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [socials, setSocials] = useState({
    instagram: '',
    facebook: '',
    tiktok: '',
    linkedin: '',
    website: '',
    youtube: '',
    twitter: ''
  });
  const [festivalData, setFestivalData] = useState<any>(null);

  useEffect(() => {
    const fetchFest = async () => {
      if (!user) return;
      const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
      try {
        const docRef = doc(db, 'festivals', activeId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFestivalData(data);
          setDescription(data.branding?.description || '');
          setCoverImage(data.branding?.coverImage || null);
          setLogoImage(data.branding?.logoImage || null);
          setGallery(data.branding?.gallery || []);
          setSocials(data.branding?.socials || {
            instagram: '',
            facebook: '',
            tiktok: '',
            linkedin: '',
            website: '',
            youtube: '',
            twitter: ''
          });
        }
      } catch (err) {
        console.error("Error fetching fest for branding", err);
      }
    };
    fetchFest();
  }, [user]);

  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 800;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(img.src);
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, setter: (val: string) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const resized = await resizeImage(file);
        setter(resized);
      } catch (err) {
        console.error("Resize failed", err);
      }
      e.target.value = '';
    }
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newImages: string[] = [];
      const filesToProcess = Array.from(files as FileList).slice(0, 6 - gallery.length);
      for (const file of filesToProcess) {
        try {
          const resized = await resizeImage(file);
          newImages.push(resized);
        } catch (err) {
          console.error("Resize failed for", file.name, err);
        }
      }
      setGallery(prev => [...prev, ...newImages]);
      e.target.value = '';
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSaving(true);
    const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
    try {
      await setDoc(doc(db, 'festivals', activeId), {
        branding: {
          description,
          coverImage,
          logoImage,
          gallery,
          socials
        }
      }, { merge: true });
      
      navigate('/create-festival/step-experience');
    } catch (error) {
      console.error("Error saving branding details", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans relative overflow-x-hidden pb-32">
      {/* Header */}
      <FestivalCreationHeader onBack={() => navigate(-1)} />

      {/* Main Content */}
      <main className="relative z-30 max-w-4xl mx-auto px-4 md:px-6 pt-4">
        
        {/* Progress Bar */}
        <div className="mb-8 md:mb-12">
          <div className="flex items-center justify-between text-xs font-black text-gray-500 mb-4 uppercase tracking-[0.2em]">
            <span>Vitrine • Étape 2/5</span>
            <span className="text-[#ff5a00]">Branding</span>
          </div>
          <div className="grid grid-cols-5 gap-1.5 md:gap-2">
            {[1, 2, 3, 4, 5].map((step) => (
              <div 
                key={step}
                className={`h-1 md:h-1.5 rounded-full transition-all duration-500 ${
                  step <= 2 
                    ? 'bg-[#FF5E00] shadow-[0_0_10px_rgba(255,94,0,0.4)]' 
                    : 'bg-white/5'
                }`}
              />
            ))}
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-8 md:space-y-12">

          {/* Marketing Tip */}
          <div className="bg-gradient-to-r from-[#FF5C00]/20 to-transparent border-l-4 border-[#FF5C00] p-4 md:p-5 rounded-r-xl flex items-start">
            <Info className="w-5 h-5 md:w-6 md:h-6 text-[#FF5C00] mr-3 shrink-0 mt-0.5" />
            <p className="text-xs md:text-base text-white/90">
              <span className="font-bold text-white">L'Impact Visuel :</span> Recevez <span className="font-black text-[#FF5C00]">4x plus de candidatures</span> avec des photos de qualité.
            </p>
          </div>

          {/* Bloc 1: ZONE MEDIA */}
          <div className="bg-[#121212] border border-white/10 rounded-[24px] md:rounded-3xl p-6 md:p-10 shadow-2xl relative overflow-hidden">
             {/* Subdued Glow effect behind */}
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[#FF5C00]/5 blur-[120px] pointer-events-none" />
             
             <div className="flex items-center space-x-3 md:space-x-4 mb-6 md:mb-8">
              <div className="p-2 md:p-3 bg-[#FF5C00]/10 border border-[#FF5C00]/20 rounded-lg md:rounded-xl">
                <ImageIcon className="w-5 h-5 md:w-6 md:h-6 text-[#FF5C00]" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">Hero Visual & Identité</h2>
            </div>

            <div className="space-y-6 md:space-y-8">
               {/* Hero Cover */}
               <div>
                 <label className="block text-[10px] md:text-sm font-bold text-gray-300 mb-2 uppercase tracking-wider">Photo de Couverture (Plein Écran)</label>
                 <div 
                   className="h-48 md:h-64 border-2 border-dashed border-white/20 hover:border-[#FF5C00]/50 rounded-xl md:rounded-2xl flex flex-col items-center justify-center bg-[#1A1A1A] hover:bg-[#151515] transition-all cursor-pointer group relative overflow-hidden text-center p-4" 
                   onClick={() => document.getElementById('coverImageUpload')?.click()}
                 >
                    <input type="file" id="coverImageUpload" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, setCoverImage)} />
                    {coverImage ? (
                      <img src={coverImage} alt="Cover Preview" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-tr from-[#111] to-[#222]" />
                    )}
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
                    <UploadCloud className="w-8 h-8 md:w-10 md:h-10 text-[#FF5C00] mb-2 md:mb-3 relative z-10 group-hover:-translate-y-2 transition-transform" />
                    <span className="font-bold text-white relative z-10 text-sm md:text-lg">Uploader le visuel principal</span>
                    <span className="text-gray-400 text-[10px] md:text-sm relative z-10 mt-1">Format paysage 16:9 recommandé</span>
                 </div>
               </div>

               {/* Logo */}
               <div>
                 <label className="block text-[10px] md:text-sm font-bold text-gray-300 mb-2 uppercase tracking-wider">Logo du Festival (1:1)</label>
                 <div className="flex items-center space-x-4 md:space-x-6">
                   <div 
                     className="w-20 h-20 md:w-32 md:h-32 border-2 border-dashed border-white/20 hover:border-[#4169E1]/50 rounded-full flex flex-col items-center justify-center bg-[#1A1A1A] hover:bg-[#151515] transition-all cursor-pointer group relative overflow-hidden shrink-0 shadow-xl"
                     onClick={() => document.getElementById('logoImageUpload')?.click()}
                   >
                      <input type="file" id="logoImageUpload" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, setLogoImage)} />
                      {logoImage ? (
                        <img src={logoImage} alt="Logo" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      ) : (
                        <ImageIcon className="w-6 h-6 md:w-8 md:h-8 text-gray-500 group-hover:text-[#4169E1] transition-colors" />
                      )}
                   </div>
                   <div>
                     <p className="text-gray-400 text-[10px] md:text-sm leading-relaxed max-w-sm">
                       Ce logo s'affichera en superposition. Fond transparent (PNG) recommandé.
                     </p>
                   </div>
                 </div>
               </div>

               {/* Gallery Grid */}
               <div>
                 <label className="block text-[10px] md:text-sm font-bold text-gray-300 mb-2 uppercase tracking-wider">Gallery Grid (Ambiance)</label>
                 <div 
                   className="border-2 border-dashed border-white/20 hover:border-[#FF5C00]/50 rounded-xl md:rounded-2xl p-4 md:p-8 flex flex-col items-center justify-center bg-[#1A1A1A] hover:bg-[#151515] transition-all cursor-pointer group" 
                   onClick={() => document.getElementById('galleryUpload')?.click()}
                 >
                   <input type="file" id="galleryUpload" className="hidden" accept="image/*" multiple onChange={handleGalleryUpload} />
                   {gallery.length > 0 ? (
                     <div className="grid grid-cols-3 md:grid-cols-6 gap-2 w-full mb-4">
                       {gallery.map((img, idx) => (
                         <div key={idx} className="aspect-square rounded-lg md:rounded-xl overflow-hidden relative border border-white/10 group/item">
                           <img src={img} alt={`Gallery ${idx}`} className="w-full h-full object-cover group-hover/item:scale-110 transition-transform" />
                           <button 
                             type="button" 
                             onClick={(e) => { e.stopPropagation(); setGallery(g => g.filter((_, i) => i !== idx)); }} 
                             className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 rounded-full p-1 transition-colors"
                           >
                             <X className="w-3 h-3 text-white" />
                           </button>
                         </div>
                       ))}
                       {gallery.length < 6 && (
                         <div className="aspect-square rounded-lg md:rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center hover:bg-white/5 transition-colors">
                           <UploadCloud className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                         </div>
                       )}
                     </div>
                   ) : (
                     <div className="text-center group-hover:-translate-y-1 transition-transform">
                       <UploadCloud className="w-8 h-8 md:w-10 md:h-10 text-[#FF5C00] mx-auto mb-3 md:mb-4" />
                       <p className="text-white font-bold text-sm md:text-base mb-1 md:mb-2">Ajouter jusqu'à 6 photos</p>
                       <p className="text-[10px] md:text-sm text-gray-500">Mettez en avant l'ambiance passée.</p>
                     </div>
                   )}
                 </div>
               </div>
            </div>
          </div>


          {/* Bloc 3: RÉSEAUX SOCIAUX */}
          <div className="bg-[#121212] border border-white/10 rounded-[24px] md:rounded-3xl p-6 md:p-10 shadow-2xl">
            <div className="flex items-center space-x-3 md:space-x-4 mb-6 md:mb-8">
              <div className="p-2 md:p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg md:rounded-xl">
                <Globe className="w-5 h-5 md:w-6 md:h-6 text-blue-500" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">Réseaux Sociaux</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-4">
                <div>
                  <label className="flex items-center text-xs font-black text-gray-500 mb-2 uppercase tracking-[0.1em]">
                    <Instagram className="w-4 h-4 mr-2 text-pink-500" /> Instagram
                  </label>
                  <input
                    type="url"
                    value={socials.instagram}
                    onChange={(e) => setSocials({ ...socials, instagram: e.target.value })}
                    placeholder="https://instagram.com/votre_festival"
                    className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-pink-500/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="flex items-center text-xs font-black text-gray-500 mb-2 uppercase tracking-[0.1em]">
                    <Facebook className="w-4 h-4 mr-2 text-blue-600" /> Facebook
                  </label>
                  <input
                    type="url"
                    value={socials.facebook}
                    onChange={(e) => setSocials({ ...socials, facebook: e.target.value })}
                    placeholder="https://facebook.com/votre_festival"
                    className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-600/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="flex items-center text-xs font-black text-gray-500 mb-2 uppercase tracking-[0.1em]">
                    <Music className="w-4 h-4 mr-2 text-white" /> TikTok
                  </label>
                  <input
                    type="url"
                    value={socials.tiktok}
                    onChange={(e) => setSocials({ ...socials, tiktok: e.target.value })}
                    placeholder="https://tiktok.com/@votre_festival"
                    className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-white/20 transition-colors"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center text-xs font-black text-gray-500 mb-2 uppercase tracking-[0.1em]">
                    <Linkedin className="w-4 h-4 mr-2 text-blue-500" /> LinkedIn
                  </label>
                  <input
                    type="url"
                    value={socials.linkedin}
                    onChange={(e) => setSocials({ ...socials, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/company/festival"
                    className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="flex items-center text-xs font-black text-gray-500 mb-2 uppercase tracking-[0.1em]">
                    <Youtube className="w-4 h-4 mr-2 text-red-600" /> YouTube / Aftermovie
                  </label>
                  <input
                    type="url"
                    value={socials.youtube}
                    onChange={(e) => setSocials({ ...socials, youtube: e.target.value })}
                    placeholder="https://youtube.com/@votre_channel"
                    className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-red-600/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="flex items-center text-xs font-black text-gray-500 mb-2 uppercase tracking-[0.1em]">
                    <LinkIcon className="w-4 h-4 mr-2 text-[#ff5a00]" /> Site Web Officiel
                  </label>
                  <input
                    type="url"
                    value={socials.website}
                    onChange={(e) => setSocials({ ...socials, website: e.target.value })}
                    placeholder="https://www.votre-festival.com"
                    className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#ff5a00]/50 transition-colors"
                  />
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-6 italic">Ces liens permettent aux exposants de mieux vous connaître et de partager leur présence à votre événement.</p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-[24px] md:rounded-3xl p-6 md:p-8 mt-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <button type="button" onClick={() => navigate(-1)} className="w-full md:w-auto px-8 py-4 rounded-xl md:rounded-2xl border border-white/10 text-white font-bold hover:bg-white/10 transition-all text-sm">Précédent</button>
              <p className="text-[10px] md:text-xs text-gray-500 font-medium text-center hidden md:block">Le modèle économique sera configuré à l'étape suivante.</p>
              <button type="submit" disabled={isSaving} className="w-full md:w-auto bg-white text-black font-black py-4 px-10 md:px-12 rounded-xl md:rounded-2xl hover:bg-[#ff5e00] hover:text-white transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer">
                {isSaving ? 'ENREGISTREMENT...' : 'CONTINUER'}
                {!isSaving && <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </form>
      </main>

      {/* PUBLIC PAGE PREVIEW MODAL */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4 md:p-8">
          <div className="bg-black w-full max-w-[1200px] h-[90vh] rounded-[2rem] border border-white/10 shadow-[0_0_100px_rgba(255,92,0,0.1)] overflow-hidden relative flex flex-col">
            
            {/* Close actions */}
            <div className="absolute top-6 right-6 z-50 flex items-center space-x-4">
               <div className="bg-black/50 backdrop-blur-md px-4 py-2 border border-white/10 rounded-full text-xs font-bold text-gray-400 tracking-widest uppercase">
                 Mode Aperçu
               </div>
               <button onClick={() => setShowPreview(false)} className="p-3 bg-black/50 hover:bg-white/10 backdrop-blur-md border border-white/10 text-white rounded-full transition-all">
                 <X className="w-6 h-6" />
               </button>
            </div>
            
            <div className="flex-1 overflow-y-auto pb-32">
              
              {/* Header Immersif */}
              <div className="h-[60vh] min-h-[500px] w-full relative">
                <img 
                  src={coverImage || "https://images.unsplash.com/photo-1540039155733-d7696d4ebc59?auto=format&fit=crop&q=80"} 
                  alt="Cover" 
                  className="w-full h-full object-cover" 
                />
                
                {/* Immersive gradient blending into dark bg */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-[#050505]/60 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent"></div>
                
                <div className="absolute bottom-12 left-8 md:left-16 flex items-end">
                  <div className="w-28 h-28 bg-[#1A1A1A] rounded-3xl border border-white/10 overflow-hidden mr-8 shadow-2xl relative z-10 flex items-center justify-center shrink-0 object-cover">
                    {logoImage ? (
                       <img src={logoImage} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                       <span className="text-xs font-black text-[#FF5C00]">LOGO</span>
                    )}
                  </div>
                  <div className="mb-2">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="px-3 py-1 bg-[#4169E1]/20 text-[#4169E1] rounded-lg text-xs font-bold uppercase tracking-wider backdrop-blur-md border border-[#4169E1]/30">Musique</span>
                      <span className="text-[#FF5C00] font-bold text-sm drop-shadow-md flex items-center"><MapIcon className="w-4 h-4 mr-1" /> Paris, France</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-black text-white shadow-black drop-shadow-2xl mb-2 tracking-tight">
                      {festivalData?.name || "Nom du Festival"}
                    </h1>
                    <div className="flex flex-wrap items-center gap-6">
                      <p className="text-xl font-medium text-gray-300 drop-shadow-md flex items-center">
                        <Calendar className="w-5 h-5 mr-2 shrink-0" />
                        {festivalData?.startDate ? new Date(festivalData.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : "Dates à venir"}
                      </p>
                      {festivalData?.ticketingUrl && (
                        <div className="bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-xl text-white font-bold text-xs flex items-center">
                          <Globe className="w-4 h-4 mr-2 text-[#FF5C00]" /> Billetterie active
                        </div>
                      )}
                      
                      {/* Social Links Preview */}
                      <div className="flex items-center space-x-3 ml-auto">
                        {socials.instagram && <Instagram className="w-5 h-5 text-pink-500 opacity-80" />}
                        {socials.facebook && <Facebook className="w-5 h-5 text-blue-600 opacity-80" />}
                        {socials.tiktok && <Music className="w-5 h-5 text-white opacity-80" />}
                        {socials.linkedin && <Linkedin className="w-5 h-5 text-blue-500 opacity-80" />}
                        {socials.website && <Globe className="w-5 h-5 text-[#ff5a00] opacity-80" />}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-8 md:px-16 -mt-4 relative z-20 max-w-7xl mx-auto space-y-16">
                 
                 {/* Dashboard Key Figures */}
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-[#1A1A1A]/80 backdrop-blur-xl border border-white/5 rounded-3xl p-8 flex flex-col items-center justify-center text-center shadow-lg hover:border-white/10 transition-all">
                       <Users className="w-10 h-10 text-[#FF5C00] mb-4" />
                       <span className="text-4xl font-black text-white tracking-tight mb-1">
                         {festivalData?.dailyPax || "0"}
                       </span>
                       <span className="text-gray-400 font-bold text-sm uppercase tracking-wider">Festivaliers / Jour</span>
                    </div>
                    {festivalData?.musicStyles && festivalData.musicStyles.length > 0 && (
                      <div className="bg-[#1A1A1A]/80 backdrop-blur-xl border border-white/5 rounded-3xl p-8 flex flex-col items-center justify-center text-center shadow-lg hover:border-white/10 transition-all">
                         <Music className="w-10 h-10 text-purple-500 mb-4" />
                         <span className="text-lg font-black text-white tracking-tight mb-1 uppercase">
                           {festivalData.musicStyles.length} Styles
                         </span>
                         <span className="text-gray-400 font-bold text-xs uppercase tracking-wider">Programmation musicale</span>
                      </div>
                    )}
                    <div className="bg-[#1A1A1A]/80 backdrop-blur-xl border border-white/5 rounded-3xl p-8 flex flex-col items-center justify-center text-center shadow-lg hover:border-white/10 transition-all">
                       <MapIcon className="w-10 h-10 text-blue-500 mb-4" />
                       <span className="text-lg font-black text-white tracking-tight mb-1 uppercase">
                         {festivalData?.terrainType || "Site extérieur"}
                       </span>
                       <span className="text-gray-400 font-bold text-sm uppercase tracking-wider">Type de terrain</span>
                    </div>
                 </div>

                 <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                   
                   {/* Left Column */}
                   <div className="lg:col-span-8 space-y-16">
                      
                      {/* Description / About */}
                      <div>
                        <h2 className="text-2xl font-black text-white mb-6 uppercase tracking-wider">À propos</h2>
                        <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-wrap mb-8">
                          {festivalData?.about || "L'histoire et les valeurs de l'événement."}
                        </p>
                        <h2 className="text-xl font-bold text-white mb-4 uppercase tracking-wider text-[#FF5C00]">L'Appel d'Offres</h2>
                        <p className="text-gray-400 text-base leading-relaxed whitespace-pre-wrap">
                          {description || "Détails de l'appel pour les stands."}
                        </p>
                      </div>

                      {/* Access Info */}
                      {festivalData?.accessInfo && (
                        <div>
                          <h2 className="text-2xl font-black text-white mb-6 uppercase tracking-wider">Accès & Mobilité</h2>
                          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-gray-300">
                             <p className="leading-relaxed">{festivalData.accessInfo}</p>
                          </div>
                        </div>
                      )}

                      {/* Map & Implantation */}
                      <div>
                        <h2 className="text-2xl font-black text-white mb-6 uppercase tracking-wider">Plan de Masse & Heatmap</h2>
                        <div className="w-full h-[400px] border border-[#4169E1]/30 rounded-3xl overflow-hidden relative group">
                           {/* Simulated Map */}
                           <img 
                             src="https://images.unsplash.com/photo-1540039155733-d7696d4ebc59?auto=format&fit=crop&q=80" 
                             alt="Map Base" 
                             className="w-full h-full object-cover mix-blend-luminosity grayscale opacity-30" 
                           />
                           <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#FF5C00]/20 via-transparent to-transparent opacity-80" />
                           <div className="absolute inset-0 flex items-center justify-center">
                              <span className="px-6 py-3 bg-[#4169E1]/20 backdrop-blur-md border border-[#4169E1]/50 text-[#4169E1] font-bold rounded-xl flex items-center shadow-[0_0_30px_rgba(65,105,225,0.4)]">
                                <Eye className="w-5 h-5 mr-2" /> Plan détaillé accessible aux candidats
                              </span>
                           </div>
                        </div>
                      </div>

                   </div>

                   {/* Right Column (Sidebar) */}
                   <div className="lg:col-span-4 space-y-8">
                     
                     {/* Logistique (Bleue) */}
                     <div className="bg-[#121212] border border-[#4169E1]/20 rounded-3xl p-8 relative overflow-hidden">
                       <div className="absolute top-0 right-0 w-32 h-32 bg-[#4169E1]/10 rounded-bl-full pointer-events-none" />
                       <h3 className="text-xl font-black text-white mb-6 uppercase tracking-wider flex items-center">
                         Logistique <Zap className="w-5 h-5 ml-2 text-[#4169E1]" />
                       </h3>
                       <ul className="space-y-6">
                          <li className="flex items-start">
                            <div className="p-3 bg-[#4169E1]/10 rounded-xl mr-4 shrink-0">
                              <Zap className="w-6 h-6 text-[#4169E1]" />
                            </div>
                            <div>
                               <div className="font-bold text-white mb-1">Réseau Électrique</div>
                               <div className="text-sm text-gray-400">Raccordements Mono 16A et Triphasé 32A/63A via borniers de zone.</div>
                            </div>
                          </li>
                          <li className="flex items-start">
                            <div className="p-3 bg-[#4169E1]/10 rounded-xl mr-4 shrink-0">
                              <Droplet className="w-6 h-6 text-[#4169E1]" />
                            </div>
                            <div>
                               <div className="font-bold text-white mb-1">Accès Fluides</div>
                               <div className="text-sm text-gray-400">Eau potable max 50m. Ramassage des eaux grises sur site.</div>
                            </div>
                          </li>
                          <li className="flex items-start">
                            <div className="p-3 bg-[#4169E1]/10 rounded-xl mr-4 shrink-0">
                              <Wifi className="w-6 h-6 text-[#4169E1]" />
                            </div>
                            <div>
                               <div className="font-bold text-white mb-1">Connectivité</div>
                               <div className="text-sm text-gray-400">Fibre TPE et Wi-Fi dédié aux terminaux de paiement.</div>
                            </div>
                          </li>
                       </ul>
                     </div>

                     {/* Emplacements Disponibles (Booking Cards) */}
                     <div className="space-y-4">
                        <h3 className="text-xl font-black text-white mb-6 uppercase tracking-wider text-[#FF5C00]">Créneaux Stands</h3>
                        <div className="flex flex-wrap gap-2 mb-8">
                          {festivalData?.allowedSlots?.map((slotId: string) => {
                             const labels: any = { 
                               day: 'Journée', 
                               night: 'Nuit', 
                               full_pass: 'Full Pass',
                               custom: festivalData?.customTimeRange || 'Plage libre'
                             };
                             return (
                               <span key={slotId} className="px-3 py-1.5 bg-[#FF5C00]/20 text-[#FF5C00] border border-[#FF5C00]/30 rounded-lg text-[10px] font-black uppercase tracking-widest">
                                 {labels[slotId] || slotId}
                               </span>
                             )
                          })}
                        </div>
                        <h3 className="text-xl font-black text-white mb-6 uppercase tracking-wider">Réserver un stand</h3>

                        {/* Card 1 */}
                        <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 hover:border-[#FF5C00]/50 transition-colors group cursor-pointer relative overflow-hidden">
                           <div className="absolute top-0 right-0 bg-[#FF5C00] text-black text-[10px] font-black uppercase px-3 py-1 rounded-bl-xl shadow-lg z-10 animate-pulse">
                             2 places restantes
                           </div>
                           <h4 className="text-lg font-bold text-white mb-2">Food Truck / Remorque</h4>
                           <div className="text-sm text-gray-400 mb-4 flex space-x-4">
                              <span>3x3m</span>
                              <span className="text-white/20">•</span>
                              <span>Pleine Terre</span>
                           </div>
                           <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5">
                             <div className="text-2xl font-black text-white">450€<span className="text-sm font-medium text-gray-500">/j</span></div>
                             <div className="text-[#FF5C00] font-bold text-sm group-hover:translate-x-1 transition-transform flex items-center">
                                Postuler <ArrowRight className="w-4 h-4 ml-1" />
                             </div>
                           </div>
                        </div>

                        {/* Card 2 */}
                        <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 hover:border-[#FF5C00]/50 transition-colors group cursor-pointer relative overflow-hidden">
                           <h4 className="text-lg font-bold text-white mb-2">Stand Découverte (Tente)</h4>
                           <div className="text-sm text-gray-400 mb-4 flex space-x-4">
                              <span>3x3m</span>
                              <span className="text-white/20">•</span>
                              <span>Herbe</span>
                           </div>
                           <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5">
                             <div className="text-2xl font-black text-white">200€<span className="text-sm font-medium text-gray-500">/j</span></div>
                             <div className="text-[#FF5C00] font-bold text-sm group-hover:translate-x-1 transition-transform flex items-center">
                                Postuler <ArrowRight className="w-4 h-4 ml-1" />
                             </div>
                           </div>
                        </div>

                     </div>

                   </div>
                 </div>
              </div>
            </div>
            
            {/* Action Bar from bottom of preview */}
            <div className="absolute bottom-0 w-full bg-black border-t border-white/10 p-6 flex flex-col items-center justify-center pointer-events-none sticky">
                <button className="bg-[#FF5C00] text-white font-black py-4 px-12 rounded-2xl flex items-center shadow-[0_0_40px_rgba(255,92,0,0.4)] pointer-events-none opacity-50">
                   <span className="mr-3">Aperçu Visuel Uniquement</span>
                </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}


